import { ChangeDetectionStrategy, Component, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Card } from '../card/card';
import { ZagIcon } from '../icon/icon';
import { ColorContrastService } from '../services/color-contrast.service';
import { ShiftDefinitionListRowData } from '../shift-definition-list-row/shift-definition-list-row';

/**
 * Re-export types for consistency
 */
export type ShiftDefinitionCardData = ShiftDefinitionListRowData;
export type { ShiftDefinitionStatusType } from '../shift-definition-list-row/shift-definition-list-row';

/**
 * ShiftDefinitionCard Component
 *
 * Displays a shift definition's basic information in a card format.
 * Uses the Card component for consistent styling.
 *
 * @example
 * ```html
 * <zag-shift-definition-card
 *   [shiftDefinition]="shiftDefinitionData"
 *   [editRoute]="['/shift-definitions', shiftDefinitionData.id]"
 *   (cardClick)="onShiftDefinitionSelected($event)">
 * </zag-shift-definition-card>
 * ```
 */
@Component({
  selector: 'zag-shift-definition-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [Card, ZagIcon],
  host: {
    '[class.zag-shift-definition-card]': 'true',
  },
  template: `
    <zag-card variant="interactive" (cardClick)="onCardClick()">
      <!-- Header: Icon/Color, Name, Status badge (below title) and optional Delete button -->
      <div card-header class="zag-shift-definition-card__header">
        <div
          class="zag-shift-definition-card__icon-avatar zag-icon-surface-gradient"
          [class.zag-shift-definition-card__icon-avatar--fallback]="!shiftDefinition().color"
          [style.background-color]="shiftDefinition().color || null"
        >
          <span
            [zagIcon]="shiftDefinition().icon || 'schedule'"
            [style.color]="getIconColor(shiftDefinition().color)"
            class="zag-shift-definition-card__icon"
          ></span>
        </div>
        <div class="zag-shift-definition-card__title-row">
          <h3 class="zag-card-title">{{ shiftDefinition().name }}</h3>
          <span
            class="zag-shift-definition-card__status"
            [class.zag-shift-definition-card__status--inactive]="shiftDefinition().status === 'inactive'"
          >
            {{ shiftDefinition().statusLabel || shiftDefinition().status }}
          </span>
        </div>
        @if (showDelete()) {
          <button
            type="button"
            class="zag-shift-definition-card__delete-btn"
            [disabled]="deleteDisabled()"
            [attr.aria-label]="deleteAriaLabel() || null"
            (click)="onDeleteClick($event)"
          >
            <span zagIcon="delete"></span>
          </button>
        }
      </div>

      <!-- Body: Time, Break, and Role info (each on its own line) -->
      <div class="zag-shift-definition-card__body">
        @if (shiftDefinition().formattedTimeRange) {
          <div class="zag-shift-definition-card__info-row">
            <span zagIcon="schedule" class="zag-shift-definition-card__info-icon"></span>
            <span class="zag-shift-definition-card__info-text">{{ shiftDefinition().formattedTimeRange }}</span>
          </div>
        }
        @if (shiftDefinition().formattedBreak) {
          <div class="zag-shift-definition-card__info-row">
            <span zagIcon="coffee" class="zag-shift-definition-card__info-icon"></span>
            <span class="zag-shift-definition-card__info-text">{{ shiftDefinition().formattedBreak }} {{ breakUnit() }}</span>
          </div>
        }
        @if (shiftDefinition().roleName !== undefined) {
          <div class="zag-shift-definition-card__info-row">
            <span zagIcon="badge" class="zag-shift-definition-card__info-icon"></span>
            <span class="zag-shift-definition-card__info-text">{{ shiftDefinition().roleName || noRoleLabel() }}</span>
          </div>
        }
      </div>
    </zag-card>
  `,
  styles: [
    `
      .zag-shift-definition-card {
        display: block;
      }

      .zag-shift-definition-card__header {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        width: 100%;
      }

      .zag-shift-definition-card__icon {
        font-size: var(--entity-avatar-icon-size-card);
        flex-shrink: 0;
      }

      .zag-shift-definition-card__icon-avatar {
        width: var(--entity-avatar-size-card);
        height: var(--entity-avatar-size-card);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .zag-shift-definition-card__icon-avatar--fallback {
        background: linear-gradient(135deg, var(--color-primary), var(--color-primary-variant));
      }

      .zag-shift-definition-card__title-row {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--spacing-xs);
        flex: 1;
        min-width: 0;
      }

      .zag-shift-definition-card__status {
        font-size: 0.75rem;
        font-weight: 500;
        padding: 2px 8px;
        border-radius: var(--border-radius-sm);
        background-color: var(--color-success-light);
        color: var(--color-success);
        white-space: nowrap;
      }

      .zag-shift-definition-card__status--inactive {
        background-color: var(--color-warning-light);
        color: var(--color-warning-dark);
      }

      .zag-shift-definition-card__body {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xs);
      }

      .zag-shift-definition-card__info-row {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
      }

      .zag-shift-definition-card__info-icon {
        font-size: 16px;
        color: var(--text-color-tertiary);
        flex-shrink: 0;
        width: 16px;
      }

      .zag-shift-definition-card__info-text {
        color: var(--text-color-secondary);
        font-size: 0.875rem;
      }

      .zag-shift-definition-card__delete-btn {
        margin-left: auto;
        align-self: flex-start;
        flex-shrink: 0;
        padding: var(--spacing-xs);
        color: var(--text-color-secondary);
        border: none;
        background: none;
        border-radius: var(--border-radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition:
          background-color 0.15s,
          color 0.15s;

        &:hover:not(:disabled) {
          background-color: var(--color-error-light);
          color: var(--color-error);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        [zagIcon] {
          font-size: 18px;
        }
      }
    `,
  ],
})
export class ShiftDefinitionCard {
  private readonly router = inject(Router);
  private readonly colorContrast = inject(ColorContrastService);

  /** The shift definition data to display */
  readonly shiftDefinition = input.required<ShiftDefinitionCardData>();

  /** Route for the edit link (array format for routerLink) */
  readonly editRoute = input<string[]>();

  /** Label prefix for role name (e.g., "Role:") */
  readonly roleLabel = input('');

  /** Label when no role is assigned */
  readonly noRoleLabel = input('—');

  /** Unit for break time (e.g., "min") */
  readonly breakUnit = input('min');

  /** Whether to show the delete button */
  readonly showDelete = input(false);

  /** Accessible label for the delete action */
  readonly deleteAriaLabel = input('');

  /** Whether the delete button is disabled */
  readonly deleteDisabled = input(false);

  /** Emitted when the card is clicked */
  readonly cardClick = output<ShiftDefinitionCardData>();

  /** Emitted when delete button is clicked */
  readonly deleteClick = output<ShiftDefinitionCardData>();

  /** Handle card click - navigates to editRoute if set, then emits cardClick */
  onCardClick(): void {
    const route = this.editRoute();
    if (route) {
      this.router.navigate(route);
    }
    this.cardClick.emit(this.shiftDefinition());
  }

  /** Handle delete button click */
  onDeleteClick(event: Event): void {
    event.stopPropagation();
    this.deleteClick.emit(this.shiftDefinition());
  }

  protected getIconColor(backgroundColor: string | null | undefined): string {
    return this.colorContrast.getIconColor(backgroundColor);
  }
}
